import React, { useState, useEffect } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { usePageContext } from 'vike-react/usePageContext';
import { detectBrowserLanguage, hasShownLocalePrompt, markLocalePromptShown } from '../utils/browser/browser-language';
import { navigate } from 'vike/client/router';
import { convertUrlToLanguage } from '../utils/algolia/url-handling';
import { useToast } from '../contexts/ToastContext';

const messages = {
  promptMessage: {
    id: 'localeMismatch.promptMessage',
    defaultMessage: 'Your browser is set to English. Would you like to switch to English?',
    description: 'Message asking if user wants to switch to English',
  },
  switchToEnglish: {
    id: 'localeMismatch.switchToEnglish',
    defaultMessage: 'Switch to English',
    description: 'Button text to switch to English',
  },
  keepCurrent: {
    id: 'localeMismatch.keepCurrent',
    defaultMessage: 'Keep {locale}',
    description: 'Button text to keep current locale',
  },
  close: {
    id: 'localeMismatch.close',
    defaultMessage: 'Close',
    description: 'Button text to close the prompt',
  },
};

// Client-only wrapper to prevent SSR issues
function LocaleMismatchPromptClient() {
  const intl = useIntl();
  const pageContext = usePageContext();
  const [isClient, setIsClient] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  
  let showToast: (message: string, type?: 'success' | 'error' | 'info', duration?: number, action?: { label: string; onClick: () => void }) => void = () => {};
  
  try {
    const toastContext = useToast();
    showToast = toastContext.showToast;
  } catch {
    // Toast context not available (SSR or not wrapped in provider)
  }

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!isClient || hasShown) return;

    // Only show on first visit
    if (hasShownLocalePrompt()) {
      return;
    }

    const currentLocale = pageContext?.locale || 'en';
    const browserLang = detectBrowserLanguage();

    // Only show if browser prefers English but user is on a different locale
    if (browserLang === 'en' && currentLocale !== 'en') {
      setHasShown(true);
      markLocalePromptShown();
      
      const handleSwitchToEnglish = async () => {
        // Get current pathname
        const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
        
        // Convert to English locale (this function currently returns the home page for the target language)
        const newPath = convertUrlToLanguage(pathname, 'en', intl);
        
        try {
          await navigate(newPath);
        } catch (error) {
          console.warn('Navigation failed, falling back to window.location:', error);
          if (typeof window !== 'undefined') {
            window.location.href = newPath;
          }
        }
      };

      // Show toast with action button
      showToast(
        intl.formatMessage(messages.promptMessage),
        'info',
        10000, // 10 seconds
        {
          label: intl.formatMessage(messages.switchToEnglish),
          onClick: handleSwitchToEnglish,
        }
      );
    }
  }, [isClient, pageContext?.locale, hasShown, intl, showToast]);

  return null;
}

// Export client-only version that only renders on client
export function LocaleMismatchPrompt() {
  // Return null during SSR
  if (typeof window === 'undefined') {
    return null;
  }
  
  return <LocaleMismatchPromptClient />;
}

